import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { App } from '../App';
import { ProtectedRoute } from '../components/routing/ProtectedRoute';
import { AuthRedirect } from '../components/routing/AuthRedirect';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { RoutingErrorBoundary } from '../components/routing/RoutingErrorBoundary';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

// Lazy load components for better performance
const Homepage = lazy(() => import('../pages/Homepage').then(m => ({ default: m.Homepage })));
const Login = lazy(() => import('../pages/Login').then(m => ({ default: m.Login })));
const DashboardV2 = lazy(() => import('../pages/DashboardV2').then(m => ({ default: m.DashboardV2 })));
const PlayerSelection = lazy(() => import('../pages/PlayerSelection').then(m => ({ default: m.PlayerSelection })));
const PerformanceAnalysis = lazy(() => import('../pages/PerformanceAnalysis').then(m => ({ default: m.PerformanceAnalysis })));
const ComparisonView = lazy(() => import('../pages/ComparisonView').then(m => ({ default: m.ComparisonView })));
const LaneAnalysis = lazy(() => import('../pages/LaneAnalysis').then(m => ({ default: m.LaneAnalysis })));
const GamePositionAnalysis = lazy(() => import('../pages/GamePositionAnalysis').then(m => ({ default: m.GamePositionAnalysis })));
const PlayerGames = lazy(() => import('../pages/PlayerGames').then(m => ({ default: m.PlayerGames })));
const Schedule = lazy(() => import('../pages/Schedule').then(m => ({ default: m.Schedule })));
const Settings = lazy(() => import('../pages/Settings').then(m => ({ default: m.Settings })));
const StandingsPage = lazy(() => import('../pages/StandingsPage').then(m => ({ default: m.StandingsPage })));
const TeamSummary = lazy(() => import('../pages/TeamSummary').then(m => ({ default: m.TeamSummary })));
const Privacy = lazy(() => import('../pages/Privacy').then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import('../pages/Terms').then(m => ({ default: m.Terms })));
const Contact = lazy(() => import('../pages/Contact').then(m => ({ default: m.Contact })));
const UnsubscribePage = lazy(() => import('../pages/UnsubscribePage').then(m => ({ default: m.UnsubscribePage })));

// Wrapper component for lazy-loaded routes with loading state and error handling
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <RoutingErrorBoundary>
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    }>
      {children}
    </Suspense>
  </RoutingErrorBoundary>
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <Homepage />
          </LazyWrapper>
        ),
      },
      {
        path: 'login',
        element: (
          <LazyWrapper>
            <Login />
          </LazyWrapper>
        ),
      },
      {
        path: 'auth-redirect',
        element: <AuthRedirect />,
      },
      {
        path: 'select-player',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <PlayerSelection />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <DashboardV2 />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'performance',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <PerformanceAnalysis />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'comparison',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <ComparisonView />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'lanes',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <LaneAnalysis />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'games',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <GamePositionAnalysis />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'player-games',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <PlayerGames />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'schedule',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <Schedule />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'standings',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <StandingsPage />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'team-summary',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <TeamSummary />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <LazyWrapper>
              <Settings />
            </LazyWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'privacy',
        element: (
          <LazyWrapper>
            <Privacy />
          </LazyWrapper>
        ),
      },
      {
        path: 'terms',
        element: (
          <LazyWrapper>
            <Terms />
          </LazyWrapper>
        ),
      },
      {
        path: 'contact',
        element: (
          <LazyWrapper>
            <Contact />
          </LazyWrapper>
        ),
      },
      {
        path: 'unsubscribe',
        element: (
          <LazyWrapper>
            <UnsubscribePage />
          </LazyWrapper>
        ),
      },
    ],
  },
]);